<script lang="ts">
  import AreaChart from './AreaChart.svelte';
  import RoleBreakdown from '@ui/RoleBreakdown.svelte';
  import ChartTooltip from '@ui/ChartTooltip.svelte';

  // The weekly-activity trend, centralized: header + legend, the dual-series area
  // chart (via AreaChart), first/last week labels, and a hover tooltip showing that
  // week's posts/comments plus a per-contributor-role breakdown (when the point
  // carries `roles`). Reused on the Overview and the member profile.
  export interface TrendPoint {
    week: string; // YYYY-MM-DD (week start)
    posts: number;
    comments: number;
    engaged?: number; // distinct non-staff members active that week (community trend only)
    roles?: Record<string, number>;
  }

  let {
    points,
    height = 150,
    title = 'Weekly activity',
    colorA = 'var(--color-primary)',
    colorB = 'var(--color-secondary)',
    colorC = 'var(--color-violet)',
    labelA = 'posts',
    labelB = 'comments',
    labelC = 'engaged members',
  }: {
    points: TrendPoint[];
    height?: number;
    title?: string;
    colorA?: string;
    colorB?: string;
    colorC?: string;
    labelA?: string;
    labelB?: string;
    labelC?: string;
  } = $props();

  const postsArr = $derived(points.map((p) => p.posts));
  const commentsArr = $derived(points.map((p) => p.comments));
  // Engaged-members line is optional (community trend has it, a single member's doesn't)
  // and sits on its OWN scale — it's a member count, not an event count.
  const hasEngaged = $derived(points.some((p) => p.engaged != null));
  const engagedArr = $derived(points.map((p) => p.engaged ?? 0));
  const maxEngaged = $derived(Math.max(1, ...engagedArr));
  const n = $derived(points.length);
  const max = $derived(Math.max(1, ...postsArr, ...commentsArr));

  function weekLabel(w: string): string {
    return new Date(w + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  // Hover: map the cursor's x to the nearest week; guide/dots share AreaChart's
  // linear (preserveAspectRatio=none) mapping so they land exactly on the lines.
  let wrapW = $state(0);
  let hover = $state<{ i: number; x: number; y: number } | null>(null);
  function move(e: MouseEvent): void {
    if (n === 0) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const rel = rect.width ? Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) : 0;
    hover = { i: Math.round(rel * (n - 1)), x: e.clientX, y: e.clientY };
  }
  const gx = $derived(hover && n > 1 ? (hover.i / (n - 1)) * wrapW : 0);
  const dotY = (v: number): number => height - (v / max) * height;
</script>

<div class="mb-3 flex items-center justify-between">
  <div class="eyebrow">{title}</div>
  <div class="flex items-center gap-3 font-mono text-[0.7rem] text-base-content/65">
    <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-sm" style:background={colorA}></span>{labelA}</span>
    <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-sm" style:background={colorB}></span>{labelB}</span>
    {#if hasEngaged}
      <span class="flex items-center gap-1"><span class="h-0.5 w-3" style:background={colorC}></span>{labelC}</span>
    {/if}
  </div>
</div>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="relative"
  style="height:{height}px"
  bind:clientWidth={wrapW}
  onmousemove={move}
  onmouseenter={move}
  onmouseleave={() => (hover = null)}
>
  <AreaChart a={postsArr} b={commentsArr} c={hasEngaged ? engagedArr : []} {height} {colorA} {colorB} {colorC} />
  {#if hover}
    <div class="pointer-events-none absolute inset-y-0 w-px bg-base-content/25" style="left:{gx}px"></div>
    <div class="pointer-events-none absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full" style="left:{gx}px; top:{dotY(postsArr[hover.i] ?? 0)}px; background:{colorA}"></div>
    <div class="pointer-events-none absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full" style="left:{gx}px; top:{dotY(commentsArr[hover.i] ?? 0)}px; background:{colorB}"></div>
    {#if hasEngaged}
      <div class="pointer-events-none absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full" style="left:{gx}px; top:{height - ((engagedArr[hover.i] ?? 0) / maxEngaged) * height}px; background:{colorC}"></div>
    {/if}
  {/if}
</div>

{#if n}
  <div class="mt-2 flex justify-between font-mono text-[0.6rem] text-base-content/62">
    <span>{weekLabel(points[0]!.week)}</span><span>{weekLabel(points.at(-1)!.week)}</span>
  </div>
{/if}

{#if hover && points[hover.i]}
  {@const p = points[hover.i]!}
  <ChartTooltip x={hover.x} y={hover.y}>
    <div class="font-mono text-[0.7rem] font-semibold">Week of {weekLabel(p.week)}</div>
    <div class="mt-0.5 text-xs text-base-content/70">
      <span class="font-semibold" style:color={colorA}>{p.posts.toLocaleString()}</span> {labelA} ·
      <span class="font-semibold" style:color={colorB}>{p.comments.toLocaleString()}</span> {labelB}
    </div>
    {#if p.engaged != null}
      <div class="mt-0.5 text-xs text-base-content/70">
        <span class="font-semibold" style:color={colorC}>{p.engaged.toLocaleString()}</span> {labelC}
      </div>
    {/if}
    {#if p.roles}
      <div class="mt-2 border-t hair pt-2">
        <RoleBreakdown roles={p.roles} total={p.engaged} label="engaged members, by role" />
      </div>
    {/if}
  </ChartTooltip>
{/if}
